// Web Mercator (EPSG:3857): camera ↔ bbox math and the clustering grid, pure so
// web and native share one implementation. The grid half MIRRORS
// `services/main/src/models/Property/queries/mapGrid.ts` — a deliberate copy (the
// backend cannot import this package); drift costs cache-hit rate, not correctness.

/** `[minLng, minLat, maxLng, maxLat]`, WGS84 — the tuple the map endpoint takes. */
export type Bbox = [number, number, number, number];

/** WGS84 semi-major axis — the sphere radius EPSG:3857 is defined against. */
const R = 6378137;

/** Full world extent in metres (2πR ≈ 40 075 016.7). */
const WORLD_METERS = 2 * Math.PI * R;

const TILE_SIZE = 256;

/** Mercator is undefined at the poles; EPSG:3857 truncates here. */
const MAX_LAT = 85.05112878;

const clampLat = (lat: number): number => Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));

/** Web Mercator world size (px) at a given zoom. */
const worldPx = (zoom: number): number => TILE_SIZE * 2 ** zoom;

/** Latitude (deg) → Mercator Y in [0, 1]. */
const latToMercY = (lat: number): number => {
    const sin = Math.sin((clampLat(lat) * Math.PI) / 180);
    return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
};

/** Mercator Y in [0, 1] → latitude (deg). */
const mercYToLat = (y: number): number => {
    const n = Math.PI - 2 * Math.PI * y;
    return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

/**
 * Latitude is non-linear under Mercator, so each edge is projected individually.
 * Tilt and bearing are ignored — these maps stay top-down and north-up.
 */
export const bboxFromCamera = (
    center: { latitude: number; longitude: number },
    zoom: number,
    widthPx: number,
    heightPx: number,
): Bbox => {
    const scale = worldPx(zoom);

    const lngSpan = (widthPx / scale) * 360;
    const minLng = center.longitude - lngSpan / 2;
    const maxLng = center.longitude + lngSpan / 2;

    const centerY = latToMercY(center.latitude);
    const yHalf = heightPx / 2 / scale;
    const maxLat = mercYToLat(centerY - yHalf); // north edge (smaller Y)
    const minLat = mercYToLat(centerY + yHalf); // south edge (larger Y)

    return [minLng, minLat, maxLng, maxLat];
};

/** Structural: matches `react-native-maps`' `Region` without depending on it. */
export interface MapRegion {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
}

/**
 * Centre ± half of each span — no projection, unlike {@link bboxFromCamera}.
 * Prefer this whenever the map reports its own region.
 */
export const bboxFromRegion = (region: MapRegion): Bbox => [
    region.longitude - region.longitudeDelta / 2,
    region.latitude - region.latitudeDelta / 2,
    region.longitude + region.longitudeDelta / 2,
    region.latitude + region.latitudeDelta / 2,
];

/** The inverse of {@link bboxFromRegion} — framing a cluster needs no zoom maths. */
export const regionFromBbox = (bbox: Bbox): MapRegion => {
    const [minLng, minLat, maxLng, maxLat] = bbox;
    return {
        latitude: (minLat + maxLat) / 2,
        longitude: (minLng + maxLng) / 2,
        // Floor: co-located properties would animate to an unrenderable region.
        latitudeDelta: Math.max(maxLat - minLat, 0.002),
        longitudeDelta: Math.max(maxLng - minLng, 0.002),
    };
};

/**
 * The zoom a region shows in a `widthPx` viewport — the inverse of
 * {@link bboxFromCamera}'s longitude half. Needed even where the bbox comes free:
 * the server's grid cell size derives from zoom, so it rides every request.
 */
export const zoomFromRegion = (longitudeDelta: number, widthPx: number): number => {
    const span = Math.max(longitudeDelta, 1e-9);
    return Math.max(0, Math.min(22, Math.log2((360 * widthPx) / (TILE_SIZE * span))));
};

/** Center of a bbox — used to re-center a camera when framing an area or place. */
export const bboxCenter = (bbox: Bbox): { latitude: number; longitude: number } => {
    const [minLng, minLat, maxLng, maxLat] = bbox;
    return { latitude: (minLat + maxLat) / 2, longitude: (minLng + maxLng) / 2 };
};

/** Bounding box (padded a touch) of a set of points, or null if there are none. */
export const bboxOfPoints = (
    points: { latitude: number; longitude: number }[],
    pad = 0.15,
): Bbox | null => {
    if (points.length === 0) return null;

    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const p of points) {
        minLng = Math.min(minLng, p.longitude);
        maxLng = Math.max(maxLng, p.longitude);
        minLat = Math.min(minLat, p.latitude);
        maxLat = Math.max(maxLat, p.latitude);
    }

    const lngPad = (maxLng - minLng) * pad || 0.02;
    const latPad = (maxLat - minLat) * pad || 0.02;
    return [minLng - lngPad, minLat - latPad, maxLng + lngPad, maxLat + latPad];
};

/** Zoom fitting a bbox into a viewport: `bboxFromCamera` inverted, tighter axis wins. */
export const zoomForBbox = (bbox: Bbox, widthPx: number, heightPx: number): number => {
    const [minLng, minLat, maxLng, maxLat] = bbox;
    const lngFraction = Math.max(Math.abs(maxLng - minLng) / 360, 1e-6);
    const latFraction = Math.max(Math.abs(latToMercY(minLat) - latToMercY(maxLat)), 1e-6);
    const zoomLng = Math.log2(widthPx / TILE_SIZE / lngFraction);
    const zoomLat = Math.log2(heightPx / TILE_SIZE / latFraction);
    return Math.max(3, Math.min(18, Math.min(zoomLng, zoomLat)));
};

// The clustering grid — mirrors the backend; see the file header.

/** Metres per grid cell at a given zoom — a 64-screen-pixel cell. */
export const cellSizeMeters = (zoom: number): number => WORLD_METERS / 2 ** (zoom + 2);

export const mercatorX = (lng: number): number => ((lng * Math.PI) / 180) * R;

export const mercatorY = (lat: number): number =>
    Math.log(Math.tan(Math.PI / 4 + (clampLat(lat) * Math.PI) / 360)) * R;

const lngFromMercatorX = (x: number): number => ((x / R) * 180) / Math.PI;

const latFromMercatorY = (y: number): number =>
    ((2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * 180) / Math.PI;

/**
 * Without it snapping is NOT idempotent: a round-tripped boundary lands a few ULPs
 * past, `Math.ceil` jumps a cell, and each pass inflates the viewport. 1e-7 clears
 * the worst-case double error 20×.
 */
const SNAP_EPSILON_CELLS = 1e-7;

const snapDown = (meters: number, cell: number): number =>
    Math.floor(meters / cell + SNAP_EPSILON_CELLS) * cell;

const snapUp = (meters: number, cell: number): number =>
    Math.ceil(meters / cell - SNAP_EPSILON_CELLS) * cell;

/** Integer cell coordinates a point falls in, on the globally-aligned grid. */
export const cellIndex = (lng: number, lat: number, zoom: number): { cx: number; cy: number } => {
    const cell = cellSizeMeters(zoom);
    return { cx: Math.floor(mercatorX(lng) / cell), cy: Math.floor(mercatorY(lat) / cell) };
};

/**
 * Rounded, clamped 0–22. Cameras report fractional zoom mid-pinch and `zoom` is in
 * both the React Query and CloudFront keys, so an unrounded 11.4000001 mints a
 * fresh entry at both layers every frame. Send THIS on the wire.
 */
export const canonicalZoom = (zoom: number): number => Math.max(0, Math.min(22, Math.round(zoom)));

/**
 * Expand OUTWARD to the enclosing cell boundaries — never inward, so the snapped
 * box always contains what the user sees. Panning within a cell then leaves bbox,
 * query key and CDN key identical; without it every camera settle mints an entry.
 */
export const snapBbox = (bbox: Bbox, zoom: number): Bbox => {
    const cell = cellSizeMeters(canonicalZoom(zoom));
    const [minLng, minLat, maxLng, maxLat] = bbox;

    return [
        lngFromMercatorX(snapDown(mercatorX(minLng), cell)),
        latFromMercatorY(snapDown(mercatorY(minLat), cell)),
        lngFromMercatorX(snapUp(mercatorX(maxLng), cell)),
        latFromMercatorY(snapUp(mercatorY(maxLat), cell)),
    ];
};
